import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { message } from 'antd';

const usePowerBiEmbed = (workspaceId, reportId, userId, endpoint , baseURL = 'https://vision.alfama.com/api') => {
  const [embedInfo, setEmbedInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRefreshTimeout = useRef(null);
  const api = useRef(axios.create({
    baseURL,
    timeout: 50000,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  })).current;

  // Função auxiliar para construir a URL completa
  const buildUrl = useCallback(() => {
    const baseEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${baseEndpoint}/${workspaceId}/${reportId}/${userId}`;
  }, [endpoint, workspaceId, reportId, userId]);

  const refreshToken = useCallback(async () => {
    try {
      const response = await api.get(buildUrl());
      
      if (response.data.success) {
        const newEmbedInfo = response.data.data;
        setEmbedInfo(prev => ({
          ...prev,
          embedToken: newEmbedInfo.embedToken
        }));
        return newEmbedInfo.embedToken;
      } else {
        throw new Error(response.data.error || 'Falha ao renovar o token');
      }
    } catch (err) {
      console.error('Erro ao renovar token:', err);
      message.error('Erro ao renovar token do relatório. Tentando novamente...');
      tokenRefreshTimeout.current = setTimeout(refreshToken, 60000);
      throw err;
    }
  }, [buildUrl, api]);

  const setupTokenRefresh = useCallback(() => {
    if (tokenRefreshTimeout.current) {
      clearTimeout(tokenRefreshTimeout.current);
    }
    
    tokenRefreshTimeout.current = setTimeout(async () => {
      await refreshToken();
      setupTokenRefresh();
    }, 3000000);
  }, [refreshToken]);

  const fetchEmbedInfo = useCallback(async () => {
    if (!workspaceId || !reportId || !userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(buildUrl());
      
      if (response.data.success) {
        setEmbedInfo(response.data.data);
        setupTokenRefresh();
      } else {
        throw new Error(response.data.error || 'Falha ao obter informações do relatório');
      }
    } catch (err) {
      console.error('Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, reportId, userId, api, setupTokenRefresh, buildUrl]);

  useEffect(() => {
    fetchEmbedInfo();

    return () => {
      if (tokenRefreshTimeout.current) {
        clearTimeout(tokenRefreshTimeout.current);
      }
    };
  }, [workspaceId, reportId, userId, endpoint]);

  const handleError = useCallback(async (event, reportRef) => {
    if (!event?.detail) return;
    
    console.error('Erro no relatório:', event.detail);
    if (event.detail.message.includes('TokenExpired') || 
        event.detail.message.includes('401') || 
        event.detail.message.includes('Unauthorized')) {
      try {
        const newToken = await refreshToken();
        if (reportRef?.current) {
          await reportRef.current.setAccessToken(newToken);
        }
      } catch (err) {
        console.error('Erro ao atualizar token:', err);
      }
    } else {
      message.error('Erro ao renderizar relatório: ' + event.detail.message);
    }
  }, [refreshToken]);

  const refreshReportWithLimit = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const key = `refresh_count_${reportId}_${today}`;
    const currentCount = parseInt(localStorage.getItem(key)) || 0;
  
    if (currentCount >= 5) {
      message.warning('Limite diário de 5 atualizações atingido para este relatório.');
      return;
    }
  
    try {
      const url = `/powerbi/workspace/${workspaceId}/report/${reportId}/refresh`;
      const response = await api.put(url);
  
      if (response.status === 202 && response.data?.message?.includes('em andamento')) {
        message.warning(`${response.data.message} (${response.data.remaining ?? '0'} restante${response.data.remaining === 1 ? '' : 's'})`);
        return;
      }
  
      if (response.data?.success) {
        localStorage.setItem(key, currentCount + 1);
        message.success(`${response.data.message} (${response.data.remaining} restante${response.data.remaining === 1 ? '' : 's'})`);
      } else {
        message.error(response.data.message || 'Erro ao atualizar dados do relatório.');
      }
    } catch (err) {
      console.error('Erro ao atualizar o relatório:', err);
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error || 'Erro ao atualizar dados do relatório.';
      message.error(apiMessage);
    }
  }, [workspaceId, reportId, api]);
  
  
  

  return {
    embedInfo,
    loading,
    error,
    fetchEmbedInfo,
    refreshToken,
    handleError,
    refreshReportWithLimit
  };
  
};

export default usePowerBiEmbed;